import { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faFileImport, faStopCircle } from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, Tools,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useCampaign, { generateUrls } from '@hooks/useCampaign'
import useCampaigns from '@hooks/useCampaigns'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import CampaignListItem from '../CampaignListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      callbacks: {
        // @ts-expect-error TS7006
        buildSearchRequestOptions: state => ({ agency_id: state.agency_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      key: 'agency_id',
    },
  ]
}

const PageContent = (props: { pageFilters: Object }) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { hidden } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden || 'false'

  const campaignsPayload = useCampaigns({
    // @ts-expect-error TS2322
    filters: { hidden: isHidden, ...pageFilters },
    performHttpRequests: true,
    requestOptions: { include_campaign_agency: true },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaigns,
    filteredCampaignsCount,
    hasFilteredCampaigns,
    loading,
  } = campaignsPayload

  const campaignPayload = useCampaign()
  const {
    callbacks: { bulkRemapCampaignPortals },
  } = campaignPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { campaignsIndexUrl, archivedCampaignsIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Campaigns | Realhub</title>
      </Helmet>

      <PageHeader
        // @ts-expect-error TS2322
        activeTabBarItemKey={isHidden}
        tabBarItems={[
          {
            href: campaignsIndexUrl,
            icon: faCheckCircle,
            key: 'false',
            title: 'Current',
          },
          {
            href: archivedCampaignsIndexUrl,
            icon: faStopCircle,
            key: 'true',
            title: 'Archived',
          },
        ]}
        title="Campaigns"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCampaigns && <ListBlankState />}

            {!loading && hasFilteredCampaigns && (
              <DashboardModule title="Campaigns">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredCampaigns.map(campaign => (
                      <CampaignListItem key={campaign.id} campaign={campaign} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredCampaignsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <Tools>
                <Button
                  buttonStyle="secondaryUtility"
                  // @ts-expect-error TS2322
                  ghost
                  icon={<FontAwesomeIcon icon={faFileImport} />}
                  onClick={() => bulkRemapCampaignPortals()}
                  size="medium"
                >
                  Portal Remapping
                </Button>
              </Tools>
            </ScopedConditionalContent>
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
